import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { SimpleGrid } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';

import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

import useDashboardContext from '../../context/useDashboardContext';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import Section from './shared/Section';
import ProgressCard from './shared/ProgressCard';
import { getWorkPlacementGroups } from './shared/workPlacementStatusGroups';

type Props = {
  data: RouterOutputs['dashboard']['getWorkPlacements'];
};

function WorkPlacementsUI({ data }: Props) {
  // Each card groups one or more raw placement statuses, except On hold, which reads the
  // onHold flag. On-hold placements are excluded from the status counts, so the cards don't overlap.
  const cards = getWorkPlacementGroups().map((group) => {
    const value =
      group.key === 'onHold'
        ? data.onHoldCount
        : group.statuses.reduce(
            (total, status) =>
              total + (data.statusCounts.find((d) => d.status === status)?.count ?? 0),
            0,
          );
    return { ...group, value };
  });

  return (
    <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={2}>
      {cards.map((card) => (
        <ProgressCard
          key={card.key}
          backgroundColour={card.backgroundColour}
          label={card.label}
          value={card.value}
        />
      ))}
    </SimpleGrid>
  );
}

function WorkPlacementsWithData() {
  const { openPanel } = useDashboardContext();
  const { filters, appliedFiltersCount } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getWorkPlacements.useQuery(
    { filters },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (data === undefined) {
    return <ErrorFallback />;
  }

  // Only surface the row for providers who actually have work placements; otherwise
  // render nothing (no section header) so it never appears for non-WP providers.
  // On-hold placements are excluded from statusCounts, so count them separately here.
  const totalPlacements =
    data.statusCounts.reduce((total, { count }) => total + count, 0) + data.onHoldCount;
  if (appliedFiltersCount === 0 && totalPlacements === 0) {
    return null;
  }

  return (
    <Section
      title={t`Work placements`}
      tooltipText={t`Status of placements for active enrolments.`}
    >
      <CardGroup cardType="muted" title={t`Status`} openPanel={() => openPanel('WorkPlacements')}>
        <WorkPlacementsUI data={data} />
      </CardGroup>
    </Section>
  );
}

export default function WorkPlacements() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={null}>
        <WorkPlacementsWithData />
      </Suspense>
    </ErrorBoundary>
  );
}
